/**
 * Filter controls shared by All Activities and YOYO Picks: the pill button with its
 * dropdown menus, removable chips, and the mobile sheet sections. One look on both pages.
 */

export function CheckboxMenu({
  options,
  selected,
  onToggle,
}: {
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-soft-gray rounded-xl shadow-xl p-2 z-30">
      {options.map((o) => (
        <label
          key={o.value}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-pale cursor-pointer text-sm"
        >
          <input
            type="checkbox"
            checked={selected.includes(o.value)}
            onChange={() => onToggle(o.value)}
            className="w-4 h-4 accent-primary"
          />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}

export function FilterButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition-colors ${
        active
          ? 'bg-primary-pale border-primary text-primary'
          : 'bg-white border-soft-gray hover:border-primary text-charcoal'
      }`}
    >
      <span>{label}</span>
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

export function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-1.5 px-3 py-1 bg-white border border-primary text-primary rounded-full text-sm">
      <span>{label}</span>
      <button
        type="button"
        aria-label="remove"
        onClick={onRemove}
        className="hover:bg-primary-pale rounded-full w-4 h-4 flex items-center justify-center text-xs"
      >
        ✕
      </button>
    </div>
  );
}

export function CheckboxSection({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <section>
      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{title}</h3>
      <div className="space-y-1">
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-3 py-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={selected.includes(o.value)}
              onChange={() => onToggle(o.value)}
              className="w-4 h-4 accent-primary"
            />
            <span className={`text-sm ${selected.includes(o.value) ? 'font-semibold' : ''}`}>
              {o.label}
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

/** Single choice in a dropdown (the When menu's look): the chosen option is green and bold. */
export function OptionMenu<T extends string>({
  options,
  selected,
  onSelect,
}: {
  options: { value: T; label: string }[];
  selected: T;
  onSelect: (value: T) => void;
}) {
  return (
    <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-soft-gray rounded-xl shadow-xl p-2 z-30">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`w-full text-left px-3 py-2.5 rounded-lg text-sm hover:bg-primary-pale ${
            selected === o.value ? 'text-primary font-semibold' : 'text-charcoal'
          }`}
          onClick={() => onSelect(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Single choice in the mobile sheet (radio rows, like its When section). */
export function RadioSection<T extends string>({
  title,
  name,
  options,
  selected,
  onSelect,
}: {
  title: string;
  name: string;
  options: { value: T; label: string }[];
  selected: T;
  onSelect: (value: T) => void;
}) {
  return (
    <section>
      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">{title}</h3>
      <div className="space-y-1">
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-3 py-2.5 cursor-pointer">
            <input
              type="radio"
              name={name}
              checked={selected === o.value}
              onChange={() => onSelect(o.value)}
              className="accent-primary"
            />
            <span className={`text-sm ${selected === o.value ? 'font-semibold' : ''}`}>
              {o.label}
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}
